import { Route, Routes } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { useNavigate } from '@/components/Link/Link';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import { useCreateRole, useRoles } from '@/models/role';

import { Filters } from './Filters/Filters';
import { FiltersStateContext, useFiltersState } from './Filters/FiltersState';
import { Role } from './Role/Role';
import { PERMISSION_LABELS } from './permission-labels';
import { SCOPE_KEYS, useRoleScopeLabels } from './scope-labels';

const ROLES_PER_PAGE = 20;

export const RolesRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<RolesList />} />
      <Route path="/:id" element={<Role />} />
    </Routes>
  );
};

const RolesList = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const filters = useFiltersState();
  const scopeLabels = useRoleScopeLabels();

  const {
    data: { roles = [], meta } = { roles: [] },
    isLoading: isRolesLoading,
    isPlaceholderData: isRolesPlaceholder,
    isError: isRolesError,
  } = useRoles(
    {
      limit: ROLES_PER_PAGE,
      offset: (filters.page.page - 1) * ROLES_PER_PAGE,
      search: filters.search.debouncedSearch,
      scopes: filters.scopes.scopes,
      permissions: filters.permissions.permissions,
    },
    {
      keepPreviousData: true,
    }
  );

  const { mutateAsync: createRole, isPending: isCreateRolePending } = useCreateRole();

  const handleCreateRole = () => {
    createRole({
      name: t({
        id: 'settings.roles.create.untitled.name',
        message: 'Untitled role',
      }),
      description: null,
      permissions: [],
      scopeOrganization: true,
      scopeStore: false,
      scopeWorkshop: false,
      scopeAfterSales: false,
    }).then((role) => {
      navigate(`/settings_beta/team/roles/${role.id}`);
    });
  };

  return (
    <main style={{ gap: '1rem' }}>
      <FiltersStateContext value={filters}>
        <Stack row gap="1rem" alignItems="flex-start" justifyContent="space-between">
          <Stack style={{ flex: 1, minWidth: 0 }}>
            <Filters />
          </Stack>
          <Button
            variant="secondary"
            size="medium"
            onPress={handleCreateRole}
            isLoading={isCreateRolePending}
          >
            <IconAdd />
            <Trans id="settings.roles.create.trigger">Create role</Trans>
          </Button>
        </Stack>
      </FiltersStateContext>

      <TableQueryWrapper isLoading={isRolesLoading || isRolesPlaceholder} isError={isRolesError}>
        {roles && roles.length === 0 ? (
          <EmptyState
            subtitle={
              <Stack>
                <Paragraph>
                  <Trans id="settings.organization.roles.no-results.1">
                    No results are matching your criteria.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans id="settings.organization.roles.no-results.2">
                    Please try with different keywords or filters.
                  </Trans>
                </Paragraph>
              </Stack>
            }
            illustration={<IllustrationNoResults />}
          />
        ) : (
          <Table
            ariaLabel={t({
              id: 'settings.roles.table.aria_label',
              message: 'Roles and permissions',
            })}
            bordered
            columnWidths="minmax(200px, 1.5fr) minmax(160px, 1fr) minmax(260px, 2fr)"
          >
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.roles.column.role">Role</Trans>
                </Column>
                <Column>
                  <Trans id="settings.roles.column.scopes">Scopes</Trans>
                </Column>
                <Column>
                  <Trans id="settings.roles.column.permissions">Permissions</Trans>
                </Column>
              </Row>
            </Header>
            <Body>
              {roles.map((role) => (
                <Row key={role.id} to={`/settings_beta/team/roles/${role.id}`}>
                  <Cell isLink>
                    <Stack gap="0.25rem">
                      <InlineText size="50" weight="bold">
                        {role.name}
                      </InlineText>
                      {role.description && (
                        <InlineText color="secondary">{role.description}</InlineText>
                      )}
                    </Stack>
                  </Cell>
                  <Cell>
                    <Stack row gap="0.25rem">
                      {SCOPE_KEYS.filter((key) => role[key]).map((key) => (
                        <Badge key={key} size="small" color="secondary" hasBorder>
                          {scopeLabels[key]}
                        </Badge>
                      ))}
                    </Stack>
                  </Cell>
                  <Cell>
                    <Stack row gap="0.25rem">
                      {role.permissions
                        .map((permission) => ({
                          key: permission,
                          label: t(PERMISSION_LABELS[permission]),
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map(({ key, label }) => (
                          <Badge key={key} size="small" color="neutral">
                            {label}
                          </Badge>
                        ))}
                    </Stack>
                  </Cell>
                </Row>
              ))}
            </Body>

            <PaginationFooter
              itemsPerPage={ROLES_PER_PAGE}
              page={filters.page.page}
              onPageChange={filters.page.setPage}
              count={meta?.count}
            />
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};
